import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
    Users, Mail, Lock, User, ArrowRight, Shield, Check
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_INFO = {
    family_basic: { name: 'Family Basic', priceMonthly: '$9.99/mo', priceYearly: '$99.99/yr', children: 'Up to 2 children' },
    family_plus: { name: 'Family Plus', priceMonthly: '$14.99/mo', priceYearly: '$149.99/yr', children: 'Up to 4 children' },
    family_complete: { name: 'Family Complete', priceMonthly: '$19.99/mo', priceYearly: '$199.99/yr', children: 'Unlimited children' }
};

const FamilyRegisterPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const selectedTier = searchParams.get('tier') || 'family_basic';
    const billingCycle = searchParams.get('billing') || 'monthly';
    
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const tierInfo = TIER_INFO[selectedTier] || TIER_INFO.family_basic;
    const displayPrice = billingCycle === 'yearly' ? tierInfo.priceYearly : tierInfo.priceMonthly;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        setLoading(true);

        try {
            // First register the parent account
            const response = await axios.post(`${BACKEND_URL}/api/family/register`, {
                email: formData.email,
                password: formData.password,
                name: formData.name,
                tier: selectedTier
            });

            // Store token
            localStorage.setItem('token', response.data.access_token);
            
            // Create Stripe checkout session
            const checkoutResponse = await axios.post(
                `${BACKEND_URL}/api/family/checkout`,
                {
                    tier: selectedTier,
                    billing_cycle: billingCycle,
                    origin_url: window.location.origin
                },
                {
                    headers: { Authorization: `Bearer ${response.data.access_token}` }
                }
            );
            
            // Redirect to Stripe checkout
            if (checkoutResponse.data.url) {
                window.location.href = checkoutResponse.data.url;
            } else {
                // If checkout failed, still go to dashboard
                navigate('/family/dashboard');
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="noise-overlay" />
            
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="w-full max-w-md"
            >
                <Card className="glass-card">
                    <CardContent className="p-8">
                        {/* Header */}
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <Shield className="w-8 h-8 text-primary" />
                            </div>
                            <h1 className="font-heading text-2xl font-semibold text-foreground mb-2">
                                Create Parent Account
                            </h1>
                            <p className="text-muted-foreground text-sm">
                                Set up your family&apos;s MoodTrack account
                            </p>
                        </div>

                        {/* Selected Plan */}
                        <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 mb-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium text-foreground">{tierInfo.name}</p>
                                    <p className="text-sm text-muted-foreground">{tierInfo.children}</p>
                                </div>
                                <div className="text-right">
                                    <p className="font-semibold text-primary">{displayPrice}</p>
                                    <p className="text-xs text-muted-foreground capitalize">{billingCycle} billing</p>
                                    <Link to="/family/pricing" className="text-xs text-primary hover:underline">
                                        Change plan
                                    </Link>
                                </div>
                            </div>
                        </div>

                        {/* Form */}
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">Your Name</Label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                    <Input
                                        id="name"
                                        data-testid="family-register-name"
                                        type="text"
                                        placeholder="Parent name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                                        className="pl-10 h-12 rounded-xl bg-muted/30 border-transparent focus:border-primary/50"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                    <Input
                                        id="email"
                                        data-testid="family-register-email"
                                        type="email"
                                        placeholder="you@example.com"
                                        value={formData.email}
                                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                                        className="pl-10 h-12 rounded-xl bg-muted/30 border-transparent focus:border-primary/50"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password">Password</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                    <Input
                                        id="password"
                                        data-testid="family-register-password"
                                        type="password"
                                        placeholder="••••••••"
                                        value={formData.password}
                                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                                        className="pl-10 h-12 rounded-xl bg-muted/30 border-transparent focus:border-primary/50"
                                        required
                                        minLength={6}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword">Confirm Password</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                    <Input
                                        id="confirmPassword"
                                        data-testid="family-register-confirm-password"
                                        type="password"
                                        placeholder="••••••••"
                                        value={formData.confirmPassword}
                                        onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                                        className="pl-10 h-12 rounded-xl bg-muted/30 border-transparent focus:border-primary/50"
                                        required
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="p-3 rounded-xl bg-destructive/10 text-destructive text-sm" data-testid="family-register-error">
                                    {error}
                                </div>
                            )}

                            <Button
                                type="submit"
                                data-testid="family-register-submit"
                                className="w-full h-12 rounded-full text-lg font-medium shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                                disabled={loading}
                            >
                                {loading ? 'Creating Account...' : 'Create Parent Account'}
                                <ArrowRight className="ml-2 w-5 h-5" />
                            </Button>
                        </form>

                        {/* Features reminder */}
                        <div className="mt-6 p-4 rounded-xl bg-muted/30">
                            <p className="text-xs text-muted-foreground mb-2">Your plan includes:</p>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                                <div className="flex items-center gap-1">
                                    <Check className="w-3 h-3 text-primary" />
                                    <span>Real-time alerts</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Check className="w-3 h-3 text-primary" />
                                    <span>Full analytics</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Check className="w-3 h-3 text-primary" />
                                    <span>Custom keywords</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Check className="w-3 h-3 text-primary" />
                                    <span>Partner approval</span>
                                </div>
                            </div>
                        </div>

                        {/* Links */}
                        <div className="mt-6 text-center text-sm">
                            <p className="text-muted-foreground">
                                Already have an account?{' '}
                                <Link to="/login" className="text-primary font-medium hover:underline">
                                    Sign in
                                </Link>
                            </p>
                            <p className="text-muted-foreground mt-2">
                                Is your child registering?{' '}
                                <Link to="/family/child-register" className="text-primary font-medium hover:underline">
                                    Child Registration
                                </Link>
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>
        </div>
    );
};

export default FamilyRegisterPage;
